/**
 * SideMenu - Light Theme Navigation
 * 
 * Eye-friendly design with:
 * - Clean white sidebar
 * - Soft hover effects
 * - Subtle accent colors
 */

import React from 'react';
import { keyframes } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { Drawer, AppBar } from './SideMenuTheme';
import { useSelector, useDispatch } from 'react-redux';
import HeaderDrawer from '../Components/HeaderDrawer';
import SideMenuItemArray from './SideMenuItemArray';
import { useTranslation } from 'react-i18next';
import LogoutIcon from '@mui/icons-material/Logout';
import { Outlet } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PlaceIcon from '@mui/icons-material/Place';
import PersonIcon from '@mui/icons-material/Person';
import Tooltip from '@mui/material/Tooltip';
import Cookies from 'universal-cookie';
import { setIsOpenSideMenu, setCode, setDirections } from '../reduxStore/SettingsReducer';
import TranslateIcon from '@mui/icons-material/Translate';
import WenkTagLogo from '../Components/WenkTagLogo';
import userImage from '../assets/img/user.png';
import '../styles/ModernStyles.css';

const cookies = new Cookies();

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to { opacity: 1; transform: translateX(0); }
`;

export default function SideMenu() {
  const [t] = useTranslation('common');
  const drawerwidth = useSelector(state => state.settingsData.drawerwidth);
  const sidemenutheme = useSelector(state => state.themeData.sidemenutheme);
  const appbarTheme = useSelector(state => state.themeData.appbar);
  const [open, setOpen] = React.useState(false);
  const dispatch = useDispatch();

  const handleDrawerOpen = () => {
    setOpen(true);
    dispatch(setIsOpenSideMenu(true));
  };

  const handleDrawerClose = () => {
    setOpen(false);
    dispatch(setIsOpenSideMenu(false));
  };

  React.useEffect(() => {
    //
  }, []);

  const pathsList = React.useMemo(() => [
    {
      id: 1,
      name: t('SIDEMENU.welcome'),
      to: 'welcome',
      component: '',
      icon: (
        <Tooltip arrow placement="right" title={!open ? <span style={{ fontFamily: 'Cairo-Medium' }}>{t('SIDEMENU.welcome')}</span> : ''}>
          <DashboardIcon className="iconSideMenu" />
        </Tooltip>
      ),
      subLink: [],
    },
    {
      id: 2,
      name: t('SIDEMENU.dynamicFields') || 'Dynamic Fields',
      to: 'dynamic-fields',
      component: '',
      icon: (
        <Tooltip arrow placement="right" title={!open ? <span style={{ fontFamily: 'Cairo-Medium' }}>{t('SIDEMENU.dynamicFields') || 'Dynamic Fields'}</span> : ''}>
          <TextFieldsIcon className="iconSideMenu" />
        </Tooltip>
      ),
      subLink: [],
    },
    {
      id: 3,
      name: t('SIDEMENU.geoExtractor') || 'Geo Extractor',
      to: 'geo-extractor',
      component: '',
      icon: (
        <Tooltip arrow placement="right" title={!open ? <span style={{ fontFamily: 'Cairo-Medium' }}>{t('SIDEMENU.geoExtractor') || 'Geo Extractor'}</span> : ''}>
          <PlaceIcon className="iconSideMenu" />
        </Tooltip>
      ),
      subLink: [],
    },
    {
      id: 4,
      name: t('SIDEMENU.profiel') || 'Profile',
      to: 'profile',
      component: '',
      icon: (
        <Tooltip arrow placement="right" title={!open ? <span style={{ fontFamily: 'Cairo-Medium' }}>{t('SIDEMENU.profiel') || 'Profile'}</span> : ''}>
          <PersonIcon className="iconSideMenu" />
        </Tooltip>
      ),
      subLink: [],
    }
  ], [open, t]);
  const languageCode = useSelector((state) => state.settingsData.code);

  const toggleLanguage = () => {
    const newLang = languageCode === 'ar' ? 'en' : 'ar';
    const newDir = newLang === 'ar' ? 'rtl' : 'ltr';
    dispatch(setCode(newLang));
    dispatch(setDirections(newDir));
    // Small timeout to allow Redux to update before potential refresh if needed, 
    // though App.js should handle reactivity.
  };



  const logout = () => {
    cookies.remove('token', { path: '/' });
    cookies.remove('language', { path: '/' });
    cookies.remove('access_type', { path: '/' });
    cookies.set('sid', null, { path: '/' });
    window.location.href = cookies.get('hostUrl');
  };

  return (
    <Box sx={{ display: 'flex', transition: 'all 0.3s ease !important' }}>
      <CssBaseline />

      {/* Light AppBar */}
      <AppBar
        position="fixed"
        open={open}
        drawerwidth={drawerwidth}
        sidemenutheme={sidemenutheme}
        appbarTheme={appbarTheme}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            justifyContent: 'space-between',
            width: '100%',
            padding: '0 0 0 20px',
          }}
        >
          {/* System Title - First (rightmost in RTL) */}
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 600,
              color: '#1E293B',
              fontFamily: 'Cairo, sans-serif',
              marginRight: 3,
            }}
          >
            {t('SIDEMENU.systemTitle') || 'نظام ادارت الموارد'}
          </Typography>
          <Toolbar
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              gap: '10px'
            }}
          >

            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                // marginRight: 3, // Add spacing from status button
              }}
            >
              <WenkTagLogo height={46} variant="light" />
            </Box>

            {/* Online Status Indicator - Last (leftmost in RTL) */}
            <Box
              sx={{
                // marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '6px 14px',
                background: 'rgba(16, 185, 129, 0.08)',
                borderRadius: 2,
                border: '1px solid rgba(16, 185, 129, 0.15)',
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#10B981',
                  boxShadow: '0 0 6px rgba(16, 185, 129, 0.4)',
                }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  color: '#10B981',
                  fontFamily: 'Cairo, sans-serif',
                  fontWeight: 500,
                }}
              >
                {t('GLOBAL.online') || 'Online'}
              </Typography>
            </Box>
          </Toolbar>
        </Box>
      </AppBar>

      {/* Light Sidebar */}
      <Drawer
        variant="permanent"
        open={open}
        drawerwidth={drawerwidth}
        sidemenutheme={sidemenutheme}
      >
        <HeaderDrawer
          open={open}
          setOpen={setOpen}
          handleDrawerClose={handleDrawerClose}
          handleDrawerOpen={handleDrawerOpen}
          getUserImage={() => userImage}
        />

        {/* Divider */}
        <Divider
          sx={{
            width: open ? '85%' : '50%',
            height: '1px',
            background: '#E2E8F0',
            margin: '12px auto',
            border: 'none',
          }}
        />

        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'start',
          justifyContent: 'space-between',
          width: '100%',
          height: '100%',
          flex: 1,
        }}>
          {/* Menu Items */}
          <Box sx={{
            width: '100%',
            padding: '8px 0',
          }}>
            <SideMenuItemArray open={open} setOpen={setOpen} pathsList={pathsList} />
          </Box>

          {/* Language & Logout Section */}
          <Box
            sx={{
              width: '100%',
              padding: '16px',
              borderTop: '1px solid #E2E8F0',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {/* Language Toggle */}
            <Box
              onClick={toggleLanguage}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: open ? 'flex-start' : 'center',
                gap: 1.5,
                padding: '12px 16px',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: 'rgba(78, 205, 196, 0.05)',
                '&:hover': {
                  background: 'rgba(78, 205, 196, 0.12)',
                  '& .lang-icon': {
                    color: '#4ECDC4',
                  },
                  '& .lang-text': {
                    color: '#4ECDC4',
                  },
                },
              }}
            >
              <TranslateIcon
                className="lang-icon"
                sx={{
                  fontSize: 22,
                  color: '#64748B',
                  transition: 'all 0.3s ease',
                }}
              />
              {open && (
                <Typography
                  className="lang-text"
                  sx={{
                    fontSize: 14,
                    color: '#64748B',
                    fontFamily: 'Cairo, sans-serif',
                    fontWeight: 600,
                    transition: 'all 0.3s ease',
                    animation: `${slideIn} 0.3s ease`,
                  }}
                >
                  {languageCode === 'ar' ? t('GLOBAL.english') : t('GLOBAL.arabic')}
                </Typography>
              )}
            </Box>

            {/* Logout Button */}
            <Box
              onClick={logout}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: open ? 'flex-start' : 'center',
                gap: 1.5,
                padding: '12px 16px',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(239, 68, 68, 0.08)',
                  '& .logout-icon': {
                    color: '#EF4444',
                  },
                  '& .logout-text': {
                    color: '#EF4444',
                  },
                },
              }}
            >
              <LogoutIcon
                className="logout-icon"
                sx={{
                  fontSize: 22,
                  color: '#64748B',
                  transform: 'rotate(180deg)',
                  transition: 'all 0.3s ease',
                }}
              />
              {open && (
                <Typography
                  className="logout-text"
                  sx={{
                    fontSize: 14,
                    color: '#64748B',
                    fontFamily: 'Cairo, sans-serif',
                    fontWeight: 500,
                    transition: 'all 0.3s ease',
                    animation: `${slideIn} 0.3s ease`,
                  }}
                >
                  {t('SIDEMENU.logout')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          padding: 3,
          marginTop: '70px',
          backgroundColor: '#F8FAFC',
          minWidth: '900px !important',
          minHeight: 'calc(100vh - 70px)',
        }}
      >
        <Outlet />
      </Box>
    </Box >
  );
}
